<template>
    <div class="nc-msg-media" :class="isOperator ? 'nc-msg-media_operator' : 'nc-msg-media_customer'">
        <div
            class="nc-msg-media__bubble"
            :class="{
                'nc-msg-media__bubble_clickable': kind !== 'audio' && (!!attachment.url || !attachment.isFileExist),
                'nc-msg-media__bubble_downloading': downloading,
            }"
            @click="handleClick"
        >
            <div v-if="!isOperator && message.client_info" class="nc-msg-media__header">
                <span class="nc-msg-media__sender-name">{{ message.client_info }}</span>
            </div>

            <!-- Аудио: встроенный плеер, viewer не открывается -->
            <new-chat-audio-player
                v-if="kind === 'audio'"
                :payload="attachment.payload"
                :payload-encoding="attachment.payloadEncoding"
                :mime-type="attachment.mimeType"
                :src="attachment.url"
                :is-operator="isOperator"
            />

            <!-- Зображення: thumbnail з hover-ефектом -->
            <div
                v-else-if="kind === 'image' && attachment.url && attachment.isFileExist && !thumbError"
                class="nc-msg-media__image-wrap"
            >
                <img
                    :src="attachment.url"
                    :alt="attachment.name"
                    class="nc-msg-media__image"
                    @error="thumbError = true"
                />
                <div class="nc-msg-media__image-overlay" aria-hidden="true"></div>
            </div>

            <!-- File chip: відео, pdf, unsupported або broken thumbnail -->
            <div v-else class="nc-msg-media__file">
                <svg
                    class="nc-msg-media__file-icon"
                    width="18"
                    height="18"
                    aria-hidden="true"
                >
                    <use :href="downloading ? '#icon-loading' : (!attachment.isFileExist ? '#icon-download' : '#icon-attach')"></use>
                </svg>
                <span class="nc-msg-media__file-name">
                    {{ !attachment.isFileExist && !downloading ? 'Кликните для загрузки файла' : (attachment.name || 'Файл') }}
                </span>
            </div>

            <div class="nc-msg-media__footer">
                <div v-if="message.platform" class="nc-msg-media__platform">
                    <new-chat-platform-icon :supplier-id="message.platform.supplier_id" />
                    <span class="nc-msg-media__platform-name">{{ message.platform.name }}</span>
                </div>
                <span v-if="message.platform" class="nc-msg-media__sep">·</span>
                <span class="nc-msg-media__time">{{ message.date_created }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import { detectFileKind, extractAttachment } from '../../utils/attachment'
import NewChatPlatformIcon from '../../shared/NewChatPlatformIcon.vue'
import NewChatAudioPlayer from './NewChatAudioPlayer.vue'
import api from '@api'

export default {
    name: 'NewChatMessageMedia',
    components: { NewChatPlatformIcon, NewChatAudioPlayer },
    props: {
        message: {
            type: Object,
            required: true,
        },
        chatId: {
            type: [String, Number],
            default: null,
        },
    },
    data() {
        return {
            thumbError: false,
            downloading: false,
        }
    },
    computed: {
        isOperator() {
            return this.message.sender_id === 'operator'
        },
        attachment() {
            return extractAttachment(this.message)
        },
        kind() {
            return detectFileKind(this.attachment.mimeType, this.attachment.name, this.attachment.apiType)
        },
    },
    methods: {
        handleClick() {
            if (this.kind === 'audio') return

            if (!this.attachment.isFileExist) {
                this._requestFileDownload()
                return
            }

            if (!this.attachment.url) return
            this.$emit('open', this.attachment)
        },

        async _requestFileDownload() {
            if (this.downloading) return

            this.downloading = true
            try {
                const result = this.chatId
                    ? await api.newChat.downloadFile(this.chatId, this.message.id)
                    : await api.newChat.downloadHistoryFile(this.message.id)

                if (result && result.link) {
                    const messagePatch = {
                        ...this.message.message,
                        link: result.link,
                        is_file_exist: true,
                        mime_type: result.mime_type || this.message.message?.mime_type,
                        type: result.type || this.message.message?.type,
                        file_name: result.file_name || this.message.message?.file_name,
                    }

                    if (this.chatId) {
                        this.$store.dispatch('newChat/updateMessage', {
                            id: this.message.id,
                            patch: { message: messagePatch },
                        })
                    } else {
                        this.$emit('file-downloaded', {
                            id: this.message.id,
                            message: messagePatch,
                        })
                    }

                    if (this.kind !== 'audio') {
                        this.$emit('open', {
                            url: result.link,
                            mimeType: result.mime_type || this.attachment.mimeType,
                            name: result.file_name || this.attachment.name,
                            apiType: result.type || this.attachment.apiType,
                            isFileExist: true,
                        })
                    }
                }
            } catch (e) {
                console.error('[media] downloadFile error', e)
            } finally {
                this.downloading = false
            }
        },
    },
}
</script>

<style lang="scss">
@import '../../../../../../gulp-src/app/scss/_variables';

.nc-msg-media {
    display: flex;
    margin-bottom: 6px;

    &_operator {
        justify-content: flex-end;

        .nc-msg-media__bubble {
            background-color: $global-dark-3a;
            border-radius: 12px 12px 2px 12px;
        }

        .nc-msg-media__file-icon {
            color: $global-white;
        }

        .nc-msg-media__file-name {
            color: $global-white;
        }

        .nc-msg-media__time {
            color: rgba(255, 255, 255, 0.6);
        }


      .nc-msg-media__platform-name {
        color: $global-white;
      }
    }

    &_customer {
        justify-content: flex-start;

        .nc-msg-media__bubble {
            background-color: $global-white-f6;
            border-radius: 12px 12px 12px 2px;
        }

        .nc-msg-media__file-icon {
            color: $global-dark-3a;
        }

        .nc-msg-media__file-name {
            color: $global-black;
        }

        .nc-msg-media__time {
            color: $global-gray;
        }
    }

    &__bubble {
        max-width: 75%;
        padding: 6px 8px;
        overflow: hidden;

        &_clickable {
            cursor: pointer;

            &:hover .nc-msg-media__image-overlay {
                opacity: 1;
            }
        }

        &_downloading {
            pointer-events: none;
            opacity: 0.6;
        }
    }

    &__header {
        margin-bottom: 4px;
    }

    &__sender-name {
        font-size: 12px;
        font-weight: 600;
        color: $global-gray;
    }

    // --- Thumbnail ---
    &__image-wrap {
        position: relative;
        border-radius: 8px;
        overflow: hidden;
        line-height: 0;
    }

    &__image {
        display: block;
        max-width: 200px;
        max-height: 160px;
        width: auto;
        height: auto;
        object-fit: cover;
        border-radius: 8px;
    }

    &__image-overlay {
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.3);
        opacity: 0;
        transition: opacity 0.15s ease;
        border-radius: 8px;
    }

    // --- File chip ---
    &__file {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 4px 2px;
        max-width: 220px;
    }

    &__file-icon {
        flex: 0 0 auto;
        fill: currentColor;
    }

    &__file-name {
        flex: 1 1 auto;
        font-size: 13px;
        line-height: 18px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    // --- Footer ---
    &__footer {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 4px;
    }

    &__platform {
        display: flex;
        align-items: center;
        gap: 4px;
        flex: 0 0 auto;
    }

    &__platform-name {
        font-size: 11px;
        line-height: 14px;
        color: inherit;
        opacity: 0.6;
        white-space: nowrap;
    }

    &__sep {
        font-size: 11px;
        line-height: 14px;
        opacity: 0.4;
        flex: 0 0 auto;
    }

    &__time {
        font-size: 11px;
        line-height: 14px;
        flex: 0 0 auto;
    }
}
</style>
